import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { canUseGateway } from "../announcementPresentation";
import { useAnnouncementConfig } from "./useAnnouncementConfig";
import { Button } from "./ui/Button";
import { Field, Input } from "./ui/Field";
import { Feedback, Panel } from "./ui/Surface";
import { Switch } from "./ui/Choice";
import "./AligoSettingsPage.css";

const normalizePhone = (value: string) => value.replace(/[^0-9]/g, "");

/**
 * only="credentials"를 주면 연결 설정 한 칸만 그린다.
 *
 * 메시지 작성 창이 이 칸을 제 안에 꺼내 쓴다 — 나머지(발송 상태·테스트 문자)는 그 창이
 * 이미 제 줄과 손잡이로 말하고 있어, 창을 한 겹 더 띄우면 같은 말이 두 번 서는 꼴이었다.
 * 칸을 복사하지 않고 같은 것을 빌려 쓴다: 두 벌이면 한쪽만 고쳐질 날이 온다.
 */
export function AligoSettingsPage({ only, config: suppliedConfig }: { only?: "credentials"; config?: ReturnType<typeof useAnnouncementConfig> } = {}) {
  const ownConfig = useAnnouncementConfig(!suppliedConfig);
  const config = suppliedConfig ?? ownConfig;
  const [remaining, setRemaining] = useState<Record<string, unknown> | null>(null);
  const [remainingError, setRemainingError] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [apiKey, setApiKey] = useState(""), [userId, setUserId] = useState(""), [sender, setSender] = useState("");
  const [testMode, setTestMode] = useState(true), [finishEnabled, setFinishEnabled] = useState(true), [showPrice, setShowPrice] = useState(false);
  const [notice, setNotice] = useState<{ tone: "error" | "success"; text: string } | null>(null);
  const [settingsNotice, setSettingsNotice] = useState<{ tone: "error" | "success"; text: string } | null>(null);

  const loadRemaining = async () => {
    setRemainingError("");
    try { setRemaining(await api.getSmsRemaining()); }
    catch (error) { setRemainingError(error instanceof Error ? error.message : "잔여 건수를 조회하지 못했습니다."); }
  };
  useEffect(() => {
    // The credentials-only panel has no balance output. The composer already
    // owns the visible balance, so do not request another one behind <details>.
    if (!only && canUseGateway(config.value)) void loadRemaining();
  }, [config.value, only]);
  useEffect(() => {
    if (!config.value) return;
    setTestMode(config.value.testMode);
    setFinishEnabled(config.value.finishNotificationsEnabled !== false);
    setShowPrice(Boolean(config.value.finishPriceIncluded));
  }, [config.value]);

  const saveSettings = async (event: FormEvent) => {
    event.preventDefault(); setSettingsBusy(true); setSettingsNotice(null);
    try {
      await api.saveSmsSettings({
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        ...(userId.trim() ? { userId: userId.trim() } : {}),
        ...(sender.trim() ? { sender: normalizePhone(sender) } : {}),
        testMode, finishNotificationsEnabled: finishEnabled, finishPriceIncluded: showPrice,
      });
      setApiKey(""); setUserId(""); setSender("");
      await config.reload();
      setSettingsNotice({ tone: "success", text: "알리고 설정을 저장했습니다." });
    } catch (error) {
      setSettingsNotice({ tone: "error", text: error instanceof Error ? error.message : "알리고 설정을 저장하지 못했습니다." });
    } finally { setSettingsBusy(false); }
  };

  const clearCredentials = async () => {
    if (settingsBusy || !window.confirm("저장된 알리고 API Key, User ID와 발신번호를 삭제하시겠습니까? 환경변수 설정이 있으면 해당 값으로 전환됩니다.")) return;
    setSettingsBusy(true); setSettingsNotice(null);
    try {
      await api.clearSmsCredentials();
      await config.reload();
      setSettingsNotice({ tone: "success", text: "저장된 자격증명을 삭제했습니다." });
    } catch (error) {
      setSettingsNotice({ tone: "error", text: error instanceof Error ? error.message : "자격증명을 삭제하지 못했습니다." });
    } finally { setSettingsBusy(false); }
  };

  const sendTest = async (event: FormEvent) => {
    event.preventDefault();
    const receiver = normalizePhone(phone);
    if (!/^01\d{8,9}$/.test(receiver)) {
      setNotice({ tone: "error", text: "휴대전화 번호를 확인해 주세요." });
      return;
    }
    setBusy(true); setNotice(null);
    try {
      await api.sendSms({ receiver, msg: `[테스트] TEDDIFY 알리고 연결 확인\n${new Intl.DateTimeFormat("ko-KR", { dateStyle: "long", timeStyle: "short" }).format(new Date())}` });
      setNotice({ tone: "success", text: config.value?.testMode ? "테스트 모드에서 발송 요청을 확인했습니다. 실제 문자는 전송되지 않았습니다." : "테스트 문자를 발송했습니다." });
      await loadRemaining();
    } catch (error) {
      setNotice({ tone: "error", text: error instanceof Error ? error.message : "테스트 문자를 발송하지 못했습니다." });
    } finally { setBusy(false); }
  };

  const configured = Boolean(config.value?.accountConfigured && config.value?.senderConfigured);
  /* 메시지 작성 창 안의 「발신 설정」 = 설정 창 줄 문법(계정 창 비밀번호 칸과 같은 부품): 왼쪽 이름·설명,
     오른쪽 입력칸·스위치, 끝 줄에 저장. 스위치는 Claude 설정의 그것(36×20) — 2026-10-19 밤 지윤 「너네 UI로 통일」. */
  if (only) return <form className="account-settings aligo-credential-rows" onSubmit={event => void saveSettings(event)} aria-busy={settingsBusy}>
    {config.error && <Feedback tone="error">{config.error}<Button size="compact" onClick={() => void config.reload()}>다시 확인</Button></Feedback>}
    {settingsNotice && <Feedback tone={settingsNotice.tone}>{settingsNotice.text}</Feedback>}
    <label className="account-settings-row"><div><span>API Key</span><p>{config.value?.apiKeyHint ? `현재 ${config.value.apiKeyHint} · 바꿀 때만 입력` : "알리고에서 발급한 API Key"}</p></div>
      <input className="account-settings-input" type="password" value={apiKey} onChange={event => setApiKey(event.target.value)} autoComplete="new-password" placeholder={config.value?.apiKeyHint || "API Key"} disabled={settingsBusy} aria-label="API Key" /></label>
    <label className="account-settings-row"><div><span>User ID</span><p>{config.value?.userIdHint ? `현재 ${config.value.userIdHint} · 바꿀 때만 입력` : "알리고 로그인 아이디"}</p></div>
      <input className="account-settings-input" value={userId} onChange={event => setUserId(event.target.value)} autoComplete="off" placeholder={config.value?.userIdHint || "User ID"} disabled={settingsBusy} aria-label="User ID" /></label>
    <label className="account-settings-row"><div><span>발신번호</span><p>{config.value?.senderHint ? `현재 ${config.value.senderHint} · 바꿀 때만 입력` : "알리고에 등록된 발신번호"}</p></div>
      <input className="account-settings-input" inputMode="tel" value={sender} onChange={event => setSender(event.target.value)} autoComplete="off" placeholder={config.value?.senderHint || "010-0000-0000"} disabled={settingsBusy} aria-label="발신번호" /></label>
    <div className="account-settings-row"><div><span>테스트 모드</span><p>실제 문자는 보내지 않고 발송 요청만 확인합니다.</p></div>
      <Switch className="st-switch" aria-label="테스트 모드" checked={testMode} onChange={event => setTestMode(event.target.checked)} disabled={settingsBusy} /></div>
    <div className="account-settings-row"><div><span>완성 작업 알림</span></div>
      <Switch className="st-switch" aria-label="완성 작업 알림" checked={finishEnabled} onChange={event => setFinishEnabled(event.target.checked)} disabled={settingsBusy} /></div>
    <div className="account-settings-row"><div><span>단가 표시</span></div>
      <Switch className="st-switch" aria-label="단가 표시" checked={showPrice} onChange={event => setShowPrice(event.target.checked)} disabled={settingsBusy} /></div>
    <div className="account-settings-row account-settings-row--end">
      {config.value?.credentialSource === "stored" && <button type="button" className="account-settings-button account-settings-button--danger" disabled={settingsBusy} onClick={() => void clearCredentials()}>저장된 자격증명 삭제</button>}
      <button type="submit" className="account-settings-button" disabled={settingsBusy}>{settingsBusy ? "저장 중…" : "설정 저장"}</button>
    </div>
    <p className="aligo-credential-note">자격증명은 서버에서 암호화해 저장하며, 화면과 API 응답에는 원문을 보이지 않습니다.</p>
  </form>;
  return <div className="aligo-settings-page">
    {config.error && <Feedback tone="error">{config.error}<Button size="compact" onClick={() => void config.reload()}>다시 확인</Button></Feedback>}
    {!only && <Panel title="발송 상태" actions={<Button size="compact" disabled={!canUseGateway(config.value)} onClick={() => void loadRemaining()}>잔여 건수 새로고침</Button>}>
      {config.loading ? <p className="aligo-loading">설정을 확인하고 있습니다.</p> : config.value && <dl className="aligo-status-grid">
        <div><dt>발송 모드</dt><dd><span className={`aligo-status-dot aligo-status-dot--${config.value.testMode ? "test" : "live"}`} />{config.value.testMode ? "테스트 모드" : "실제 발송"}</dd></div>
        <div><dt>알리고 계정</dt><dd>{configured ? "설정됨" : "확인 필요"}</dd></div>
        <div><dt>설정 위치</dt><dd>{config.value.credentialSource === "stored" ? "관리자 설정" : config.value.credentialSource === "environment" ? "서버 환경변수" : "미설정"}</dd></div>
        <div><dt>완성 작업 알림</dt><dd>{config.value.finishNotificationsEnabled === false ? "사용 안 함" : "사용"}</dd></div>
        <div><dt>문자 단가 표시</dt><dd>{config.value.finishPriceIncluded ? "표시" : "표시 안 함"}</dd></div>
        <div className="aligo-balance"><dt>잔여 건수</dt><dd>{remainingError || (remaining ? `SMS ${String(remaining.SMS_CNT ?? "-")}건 · LMS ${String(remaining.LMS_CNT ?? "-")}건 · MMS ${String(remaining.MMS_CNT ?? "-")}건` : "-")}</dd></div>
      </dl>}
    </Panel>}
    <Panel title="연결 설정">
      <form className="aligo-credentials-form" onSubmit={event => void saveSettings(event)} aria-busy={settingsBusy}>
        {settingsNotice && <Feedback tone={settingsNotice.tone}>{settingsNotice.text}</Feedback>}
        <div className="aligo-credential-fields">
          <Field label="API Key" hint={config.value?.apiKeyHint ? `현재 ${config.value.apiKeyHint} · 변경할 때만 입력` : "알리고에서 발급한 API Key"}><Input type="password" value={apiKey} onChange={event => setApiKey(event.target.value)} autoComplete="new-password" placeholder={config.value?.apiKeyHint || "API Key"} disabled={settingsBusy} /></Field>
          <Field label="User ID" hint={config.value?.userIdHint ? `현재 ${config.value.userIdHint} · 변경할 때만 입력` : "알리고 로그인 아이디"}><Input value={userId} onChange={event => setUserId(event.target.value)} autoComplete="off" placeholder={config.value?.userIdHint || "User ID"} disabled={settingsBusy} /></Field>
          <Field label="발신번호" hint={config.value?.senderHint ? `현재 ${config.value.senderHint} · 변경할 때만 입력` : "알리고에 등록된 발신번호"}><Input inputMode="tel" value={sender} onChange={event => setSender(event.target.value)} autoComplete="off" placeholder={config.value?.senderHint || "010-0000-0000"} disabled={settingsBusy} /></Field>
        </div>
        <div className="aligo-policy-fields">
          <Switch label="테스트 모드" checked={testMode} onChange={event => setTestMode(event.target.checked)} disabled={settingsBusy} />
          <Switch label="완성 작업 알림" checked={finishEnabled} onChange={event => setFinishEnabled(event.target.checked)} disabled={settingsBusy} />
          <Switch label="단가 표시" checked={showPrice} onChange={event => setShowPrice(event.target.checked)} disabled={settingsBusy} />
        </div>
        <div className="aligo-settings-actions">
          <Button type="submit" variant="primary" busy={settingsBusy}>{settingsBusy ? "저장 중…" : "설정 저장"}</Button>
          {config.value?.credentialSource === "stored" && <Button type="button" variant="danger" disabled={settingsBusy} onClick={() => void clearCredentials()}>저장된 자격증명 삭제</Button>}
        </div>
      </form>
    </Panel>
    {!only && <Panel title="테스트 문자">
      <form className="aligo-test-form" onSubmit={event => void sendTest(event)}>
        {notice && <Feedback tone={notice.tone}>{notice.text}</Feedback>}
        <Field label="수신번호"><Input inputMode="tel" autoComplete="tel" value={phone} onChange={event => setPhone(event.target.value)} placeholder="010-0000-0000" disabled={busy} /></Field>
        <Button type="submit" variant="primary" busy={busy} disabled={config.loading || !canUseGateway(config.value)}>{busy ? "발송 중…" : "테스트 문자 발송"}</Button>
      </form>
    </Panel>}
    <p className="aligo-environment-note">자격증명은 서버에서 암호화하여 저장하며 화면과 API 응답에는 원문을 표시하지 않습니다.</p>
  </div>;
}
